import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { CampaignStatus, RecipientStatus } from '@/services/crmCampaignsService'

type Tone = 'neutral' | 'success' | 'warning' | 'destructive' | 'primary'

const TONES: Record<Tone, string> = {
  neutral: '',
  primary: 'border-primary/30 bg-primary/10 text-primary',
  success: 'border-success/30 bg-success/10 text-success',
  warning: 'border-warning/30 bg-warning/10 text-warning',
  destructive: 'border-destructive/30 bg-destructive/10 text-destructive',
}

const CAMPAIGN_TONES: Record<CampaignStatus, Tone> = {
  borrador: 'neutral',
  activa: 'success',
  pausada: 'warning',
  terminada: 'primary',
  archivada: 'neutral',
}

const RECIPIENT_TONES: Record<RecipientStatus, Tone> = {
  pendiente: 'neutral',
  en_curso: 'primary',
  respondido: 'success',
  completado: 'success',
  baja: 'warning',
  parado_gestion_agente: 'warning',
  parado_demanda_baja: 'warning',
  descartado_rgpd: 'warning',
  excluido: 'neutral',
  fallido: 'destructive',
}

function ToneBadge({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  return (
    <Badge variant="outline" className={cn('whitespace-nowrap font-medium', TONES[tone])}>
      {children}
    </Badge>
  )
}

export function CampaignStatusChip({ status }: { status: CampaignStatus }) {
  const { t } = useTranslation()
  return <ToneBadge tone={CAMPAIGN_TONES[status] ?? 'neutral'}>{t(`campaigns.status.${status}`)}</ToneBadge>
}

export function RecipientStatusChip({ status }: { status: RecipientStatus }) {
  const { t } = useTranslation()
  return <ToneBadge tone={RECIPIENT_TONES[status] ?? 'neutral'}>{t(`campaigns.recipient_status.${status}`)}</ToneBadge>
}

export function ExclusionChip({ reason }: { reason: string }) {
  const { t } = useTranslation()
  return (
    <ToneBadge tone="warning">
      {t(`campaigns.exclusion_reason.${reason}`, { defaultValue: reason })}
    </ToneBadge>
  )
}
